import React, { FC, useState } from "react";

import axios from "axios";
import * as yup from "yup";

import { useFormik } from "formik";
import { Link } from "gatsby";

import Icon from "@components/common/Icon";
import Spinner from "@components/common/Spinner";

import FacebookIcon from "@assets/svg/social/facebook.svg";
import InstagramIcon from "@assets/svg/social/instagram.svg";
import XIcon from "@assets/svg/social/x.svg";

import paymentMethods from "../images/payment-methods.png";

interface FormValues {
  email: string;
}

const footerLinksSections = [
  {
    sectionTitle: "Company",
    links: [
      {
        title: "Our Story",
        to: "/about-us",
      },
      {
        title: "Order Tracking",
        to: "/track-order",
      },
      {
        title: "Contact Us",
        to: "/contact-us",
      },
      {
        title: "Products",
        to: "/shop",
      },
    ],
  },
  {
    sectionTitle: "Information",
    links: [
      {
        title: "Refund Policy",
        to: "/faqs/#refund-policy",
      },
      {
        title: "Shipping & Handling",
        to: "/faqs/#shipping-and-delivery",
      },
      {
        title: "FAQs",
        to: "/faqs",
      },
      {
        title: "Privacy Policy",
        to: "/privacy-policy",
      },
      {
        title: "Cancel Subscription",
        to: "mailto:support@officialgroove.com",
      },
    ],
  },
];

const validationSchema = yup
  .object()
  .shape({ email: yup.string().email("Invalid email address").required("Email is required") });

const Footer: FC = () => {
  const initialValues: FormValues = { email: "" };
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values: FormValues): Promise<void> => {
    setIsLoading(true);

    await axios
      .get(`${window.location.origin}/api/subscribe/${values.email}`)
      .then(() => {
        setIsLoading(false);
        setIsSubmitted(true);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error.response);
      });
  };
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <footer className="footer-container">
      <div className="main-footer">
        <div className="company-info">
          <Icon name="logo" className="logo" />
          <p className="description">Get Your Groove On.</p>
          <p className="contacts">
            <span>support@officialgroove.com</span>
          </p>
        </div>

        <div className="newsletter">
          <p className="title">Newsletter</p>
          <p className="description">
            Join Our Mailing List For Awesome Updates & Unlock Special Discounts!
          </p>
          <form onSubmit={formik.handleSubmit} className="input-group-form">
            <input
              type="text"
              id="email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Your Email Address"
              disabled={isSubmitted}
            />
            <button type="submit" disabled={isSubmitted}>
              <div className="icon">{isLoading ? <Spinner /> : <Icon name="sm-arrow-right" />}</div>
            </button>
          </form>
          <div className="message-container">
            {formik.touched.email && formik.errors.email ? (
              <div className="error-message">{formik.errors.email}</div>
            ) : null}

            {isSubmitted ? <div className="success-message">Done!</div> : null}
          </div>

          <div className="social-links-container">
            <a
              href="https://www.instagram.com/groovesuperfoods/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              <InstagramIcon />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61551055355932&mibextid=LQQJ4d"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              <FacebookIcon />
            </a>
            <a
              href="https://x.com/groovesuperfood"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              <XIcon />
            </a>
          </div>
        </div>
        <div className="links-sections">
          {footerLinksSections.map(({ sectionTitle, links }) => (
            <div className="links-section" key={sectionTitle}>
              <p className="section-title">{sectionTitle}</p>
              <div className="links">
                {links.map(({ title, to }) => (
                  <Link to={to} key={title} className="section-link">
                    {title}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="disclaimer">
        <p>
          These statements have not been evaluated by the Food and Drug Administration. This product
          is not intended to diagnose, treat, cure, or prevent any disease.
        </p>
        <p>
          Use only as directed. The information provided on this site is for informational purposes
          only and is not a substitute for professional medical advice. Always consult a qualified
          health care professional before starting any new dietary supplement regimen or making
          changes to your existing health care routine. Results shared by customers reflect
          individual experiences and are not guaranteed. Some medical professionals featured or
          quoted may have been compensated for their opinions.
        </p>
      </div>
      <div className="sub-footer">
        <span className="company">{`OFFICIAL GROOVE LLC © ${new Date().getFullYear()}`}</span>
        <div className="payments-container">
          <span>Pay securely with</span>
          <img
            src={paymentMethods}
            alt="payment methods"
            className="payment-methods"
            width={304}
            height={30}
          />
        </div>
        <div className="links">
          <span className="company">{`Groove © ${new Date().getFullYear()}`}</span>
          <a href="mailto:support@officialgroove.com" className="sub-footer-link">
            Cancel Subscription
          </a>
          <Link to="/terms-and-conditions" className="sub-footer-link">
            Terms & Conditions
          </Link>
          <Link to="/privacy-policy" className="sub-footer-link">
            Privacy Policy
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
